// Shared content schema + defaults for the public prints homepage.
// Lives in one place so both Home.tsx (renderer) and the admin editor
// stay in sync. The admin Settings → Evrst Printpage page writes to
// site_settings.prints_page; Home.tsx reads it on mount and merges over
// these defaults so missing fields never blow up.
//
// DO NOT DELETE site_settings.prints_page in migrations or maintenance
// scripts. Admin-uploaded image URLs live in that row — wiping the row
// orphans them and breaks the homepage hero / look-inside imagery.
// Updates should always be UPDATEs (or no-ops if the field hasn't moved).
// This is the source of truth for what's shown to customers; defaults
// here are only the bootstrap fallback for a fresh install.

export interface PrintsHero {
  eyebrow: string
  titleLine1: string
  titleLine2: string
  body: string
  primaryCtaLabel: string
  secondaryCtaLabel: string
  imageUrl: string
}
export interface PrintsPathCard { num: string; title: string; body: string; ctaLabel: string }
export interface PrintsPath {
  eyebrow: string
  titleLine1: string
  titleLine2: string
  body: string
  card01: PrintsPathCard
  card02: PrintsPathCard
}
export interface PrintsStep { num: string; title: string; body: string }
export interface PrintsHowItWorks {
  eyebrow: string
  titleLine1: string
  titleLine2: string
  intro: string
  steps: PrintsStep[]
  ctaLabel: string
}
export interface PrintsLookInside { eyebrow: string; title: string; body: string; imageUrl: string }
export interface PrintsFaqItem { q: string; a: string }
export interface PrintsFaq { eyebrow: string; title: string; items: PrintsFaqItem[] }
export interface PrintsFinalCta {
  eyebrow: string
  titleLine1: string
  titleLine2: string
  body: string
  primaryCtaLabel: string
  secondaryCtaLabel: string
}
export interface PrintsFooter { copyrightSuffix: string; contactEmail: string; parentUrl: string }

export interface PrintsPageContent {
  hero: PrintsHero
  path: PrintsPath
  garments: PrintsHowItWorks
  lookInsideGarments: PrintsLookInside
  dtf: PrintsHowItWorks
  lookInsideDtf: PrintsLookInside
  faq: PrintsFaq
  finalCta: PrintsFinalCta
  footer: PrintsFooter
}

export const PRINTS_PAGE_DEFAULTS: PrintsPageContent = {
  hero: {
    eyebrow: 'Custom Apparel + DTF Transfers · Australian Studio',
    titleLine1: 'Custom prints,',
    titleLine2: 'on demand.',
    body: 'Design custom apparel or order press-ready DTF transfer sheets, online and in minutes. Upload your artwork, set sizes, see a live proof, and we print and ship from our Australian studio. No setup fees, no minimums, no waiting on a quote.',
    primaryCtaLabel: 'Design a Garment',
    secondaryCtaLabel: 'Build a DTF Sheet',
    imageUrl: '/previews/garment-designer.png',
  },
  path: {
    eyebrow: 'Two ways to order',
    titleLine1: 'Two paths,',
    titleLine2: 'One studio.',
    body: 'Order finished printed apparel, or press-ready DTF transfer sheets to apply yourself. Same browser-based design tools, same Australian studio, dispatched the same week.',
    card01: { num: '01', title: 'Custom Garments', body: 'Premium tees, hoodies, polos and headwear. Add your artwork, set colour, size and quantity per size — we print, finish and ship the garment ready to wear.', ctaLabel: 'Start a Garment' },
    card02: { num: '02', title: 'DTF Transfers',   body: 'Heat-press-ready direct-to-film sheets for any garment, fabric, or surface that can take a transfer. Pay by the centimetre, order from one sheet up to thousands.', ctaLabel: 'Start a DTF Sheet' },
  },
  garments: {
    eyebrow: 'How it works · Custom Garments',
    titleLine1: 'From blank tee',
    titleLine2: 'to finished print.',
    intro: 'A four-step flow that takes you from idea to confirmed order in minutes. No print files required up front — bring a logo, an illustration, or just an idea.',
    steps: [
      { num: 'Step 1', title: 'Pick your garment', body: 'Choose from premium tees, hoodies, polos and headwear. Set colour, size, and quantity per size in one panel.' },
      { num: 'Step 2', title: 'Add your design',   body: 'Upload artwork or pick from our design library. Add text, drag to place, scale, and rotate with a live on-garment preview.' },
      { num: 'Step 3', title: 'Place your order',  body: 'Secure online checkout with instant confirmation. We email a digital proof before production opens — nothing prints until you approve it.' },
      { num: 'Step 4', title: 'We print + ship',   body: 'Hand-printed in our Australian studio. Tracked dispatch lands in your inbox the moment your order leaves us.' },
    ],
    ctaLabel: 'Open the Garment Designer',
  },
  lookInsideGarments: {
    eyebrow: 'Inside the studio',
    title: 'The Garment Designer',
    body: 'A pro toolset that feels like a sketchbook. Pick a garment, drop in your design, and see exactly how it will print before you commit.',
    imageUrl: '/previews/garment-designer.png',
  },
  dtf: {
    eyebrow: 'How it works · DTF Transfers',
    titleLine1: 'Press-ready',
    titleLine2: 'DTF transfers.',
    intro: 'Upload a single design, or pack a full sheet with multiple artworks using the DTF Builder. Pay by the centimetre and ship to your studio, workshop, or home.',
    steps: [
      { num: 'Step 1', title: 'Upload or build',  body: 'Upload one artwork file, or open the DTF Builder and fill a rolling 57 cm canvas with multiple designs.' },
      { num: 'Step 2', title: 'Set your sheets',  body: 'Add as many sheets as you need. Live preview, automatic DPI feedback, and a clear running total in dollars and centimetres.' },
      { num: 'Step 3', title: 'Place your order', body: 'Secure online checkout. We confirm sheet specs by email before going to press.' },
      { num: 'Step 4', title: 'We press + ship',  body: 'Sheets printed, cured, packed, and dispatched ready to heat-press onto your own apparel, bags, or merch.' },
    ],
    ctaLabel: 'Open the DTF Builder',
  },
  lookInsideDtf: {
    eyebrow: 'Inside the studio',
    title: 'The DTF Builder',
    body: 'A rolling canvas, multi-sheet management, automatic DPI feedback. Fill it up with as many artworks as you can fit, pay by the centimetre, and we press to order.',
    imageUrl: '/previews/dtf-builder.png',
  },
  faq: {
    eyebrow: 'FAQ',
    title: 'Quick answers.',
    items: [
      { q: 'What is a DTF transfer?', a: 'DTF (direct-to-film) is a heat-press graphic printed onto a special film, ready to apply to almost any fabric — cotton, polyester, blends, denim, even canvas. You get vivid colours, soft hand-feel, and excellent wash durability without screen-printing setup fees. Order one sheet or thousands; we handle the press, you handle the application.' },
      { q: 'Do I need an account to order?', a: 'No. Both the Garment Designer and the DTF Builder work as a guest — design, checkout, done. You\'ll need an account to save designs across sessions and pick up tracking inside your dashboard, but guest orders still get an email receipt and tracking link.' },
      { q: 'What file formats can I upload?', a: 'PNG or SVG with a transparent background gives the cleanest result. JPG works for full-bleed designs. We automatically check DPI against the placement on your garment or DTF sheet and warn you if it falls under 200 — anything below that won\'t hold sharp on press.' },
      { q: 'Where do you ship from, and how long does it take?', a: 'Every order is printed in our Australian studio and dispatched via tracked courier. Standard turnaround is 3–5 business days from artwork approval to dispatch. International shipping is available by quote — message us before ordering for an accurate rate.' },
      { q: 'Are there minimum orders or setup fees?', a: 'No minimums, no setup, no per-colour charges. Order a single tee, a single DTF sheet, or thousands. Per-unit pricing stays flat; volume discounts kick in from 12 units on garments.' },
      { q: 'Can I see a proof before you print?', a: 'Yes. Every order — whether garment or DTF sheet — generates a digital proof at checkout. We don\'t open production until you approve it; revisions are free.' },
    ],
  },
  finalCta: {
    eyebrow: 'Ready when you are',
    titleLine1: 'Open a builder.',
    titleLine2: 'Start the press.',
    body: 'No setup fees, no minimums, no waiting on a quote. Pick a flow, upload your artwork, and we will handle the rest from press to doorstep.',
    primaryCtaLabel: 'Design a Garment',
    secondaryCtaLabel: 'Build a DTF Sheet',
  },
  footer: {
    copyrightSuffix: 'EVRST Studio Prints',
    contactEmail: 'hello@evrststudio.com',
    parentUrl: 'https://evrststudio.com',
  },
}

// Merge admin-saved values over the defaults, recursively. Missing keys in
// the saved value fall back to the default at that path. Arrays are taken
// verbatim from the saved value when present.
export function mergePrintsContent(saved: unknown): PrintsPageContent {
  if (!saved || typeof saved !== 'object') return PRINTS_PAGE_DEFAULTS
  const out = JSON.parse(JSON.stringify(PRINTS_PAGE_DEFAULTS)) as PrintsPageContent
  function walk(target: Record<string, unknown>, src: Record<string, unknown>) {
    for (const [k, v] of Object.entries(src)) {
      if (v == null) continue
      if (Array.isArray(v)) target[k] = v
      else if (typeof v === 'object' && typeof target[k] === 'object' && !Array.isArray(target[k]))
        walk(target[k] as Record<string, unknown>, v as Record<string, unknown>)
      else target[k] = v
    }
  }
  walk(out as unknown as Record<string, unknown>, saved as Record<string, unknown>)
  return out
}
